import { useEffect, useRef, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { supabase } from "../supabase";

/**
 * SupabaseAuthForm — custom email/password + magic-link + Google sign-in.
 *
 * Supabase Auth sign-in / sign-up form with a React 19 compatible form that
 * follows the Classic design system (CSS variables from index.css).
 *
 * Supabase Auth has CAPTCHA protection enabled, so email/password and magic-link
 * submit a Cloudflare Turnstile token via `options.captchaToken`. Google OAuth
 * uses a top-level redirect and does not require the CAPTCHA token.
 *
 * @param {Object} props
 * @param {'sign_in'|'sign_up'} [props.mode='sign_in']
 * @param {string} [props.tenantId] - Optional tenant for post-auth redirect path.
 * @param {string} [props.redirectTo] - Explicit post-auth redirect URL.
 */
export default function SupabaseAuthForm({ mode = "sign_in", tenantId, redirectTo }: {
    mode?: "sign_in" | "sign_up";
    tenantId?: string;
    redirectTo?: string;
}) {
    const { t } = useTranslation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const turnstileRef = useRef<TurnstileInstance | undefined>(undefined);

    useEffect(() => {
        if (typeof document !== "undefined") {
            document.documentElement.classList.remove("dark");
        }
    }, []);

    const isSignUp = mode === "sign_up";
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const effectiveRedirect =
        redirectTo || (tenantId ? `${origin}/portal/${tenantId}/dashboard` : `${origin}/`);
    const siteKey =
        import.meta.env.VITE_SUPABASE_TURNSTILE_SITE_KEY || import.meta.env.VITE_TURNSTILE_SITE_KEY;

    function resetCaptcha() {
        setCaptchaToken(null);
        if (turnstileRef.current && typeof turnstileRef.current.reset === "function") {
            try {
                turnstileRef.current.reset();
            } catch {
                // The widget container may already be unmounted (e.g. reset after
                // successful login navigates away) — Turnstile throws
                // "Nothing to reset found for provided container" (Sentry
                // ADAPTHUB-C). Safe to ignore: the widget is gone, so is the token.
            }
        }
    }

    function mapAuthError(err: { message?: string } | null | undefined) {
        const msg = err?.message || "";
        if (/invalid login credentials/i.test(msg)) return t("auth.invalidCredentials");
        if (/email not confirmed/i.test(msg)) return t("auth.emailNotConfirmed");
        if (/already registered|user already/i.test(msg)) return t("auth.alreadyRegistered");
        if (/captcha/i.test(msg)) return t("auth.captchaRequired");
        console.warn("[SupabaseAuthForm] auth error:", err);
        return t("auth.genericError");
    }

    async function handlePasswordSubmit(e: FormEvent) {
        e.preventDefault();
        setError("");
        setMessage("");
        if (!captchaToken) {
            setError(t("auth.captchaRequired"));
            return;
        }
        setLoading(true);
        try {
            const { error: err } = isSignUp
                ? await supabase.auth.signUp({
                    email,
                    password,
                    options: { emailRedirectTo: effectiveRedirect, captchaToken },
                })
                : await supabase.auth.signInWithPassword({
                    email,
                    password,
                    options: { captchaToken },
                });
            resetCaptcha();
            if (err) {
                setError(mapAuthError(err));
                return;
            }
            if (isSignUp) {
                // Email confirmations may be enabled: the session is not created until
                // the user confirms the email. Show a neutral confirmation message.
                setMessage(t("auth.checkEmail"));
            }
            // On sign-in success, onAuthStateChange in useAuth flips isSignedIn.
        } finally {
            setLoading(false);
        }
    }

    async function handleMagicLink() {
        if (!email) {
            setError(t("auth.emailRequired"));
            return;
        }
        if (!captchaToken) {
            setError(t("auth.captchaRequired"));
            return;
        }
        setError("");
        setMessage("");
        setLoading(true);
        try {
            const { error: err } = await supabase.auth.signInWithOtp({
                email,
                options: { emailRedirectTo: effectiveRedirect, captchaToken },
            });
            resetCaptcha();
            if (err) {
                setError(mapAuthError(err));
                return;
            }
            setMessage(t("auth.magicLinkSent"));
        } finally {
            setLoading(false);
        }
    }

    async function handleResetPassword() {
        if (!email) {
            setError(t("auth.emailRequired"));
            return;
        }
        if (!captchaToken) {
            setError(t("auth.captchaRequired"));
            return;
        }
        setError("");
        setMessage("");
        setLoading(true);
        try {
            const resetRedirect = `${origin}/portal/reset-password`;
            const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: resetRedirect,
                captchaToken,
            });
            resetCaptcha();
            if (err) {
                setError(mapAuthError(err));
                return;
            }
            setMessage(t("auth.resetEmailSent"));
        } finally {
            setLoading(false);
        }
    }

    async function handleGoogle() {
        setError("");
        setMessage("");
        setLoading(true);
        try {
            const { error: err } = await supabase.auth.signInWithOAuth({
                provider: "google",
                options: {
                    redirectTo: effectiveRedirect,
                    queryParams: { access_type: "offline", prompt: "consent" },
                },
            });
            if (err) setError(mapAuthError(err));
        } finally {
            setLoading(false);
        }
    }

    async function handlePasskeySignIn() {
        setError("");
        setMessage("");
        if (!captchaToken) {
            setError(t("auth.captchaRequired"));
            return;
        }
        setLoading(true);
        try {
            const { error: err } = await supabase.auth.signInWithPasskey({
                options: { captchaToken },
            });
            if (err) setError(mapAuthError(err));
            // On success, onAuthStateChange in useAuth flips isSignedIn.
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="w-full max-w-sm mx-auto">
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div>
                    <label className="block text-xs font-black uppercase tracking-wider text-slate-600 mb-1">
                        {t("auth.email")}
                    </label>
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                        required
                        placeholder={t("auth.email")}
                        className="w-full px-4 py-3 rounded-xl bg-white border border-slate-300 text-sm text-slate-900 placeholder:text-slate-400 outline-hidden focus:ring-2 focus:ring-[#859F4A]/30 focus:border-[#859F4A] transition-all shadow-xs"
                    />
                </div>

                <div>
                    <label className="block text-xs font-black uppercase tracking-wider text-slate-600 mb-1">
                        {t("auth.password")}
                    </label>
                    <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete={isSignUp ? "new-password" : "current-password"}
                        required
                        minLength={isSignUp ? 6 : undefined}
                        placeholder={t("auth.password")}
                        className="w-full px-4 py-3 rounded-xl bg-white border border-slate-300 text-sm text-slate-900 placeholder:text-slate-400 outline-hidden focus:ring-2 focus:ring-[#859F4A]/30 focus:border-[#859F4A] transition-all shadow-xs"
                    />
                </div>

                {error && <p className="text-xs font-bold text-rose-500">{error}</p>}
                {message && <p className="text-xs font-bold text-emerald-500">{message}</p>}

                {siteKey && (
                    <div className="flex justify-center">
                        <Turnstile
                            ref={turnstileRef}
                            siteKey={siteKey}
                            options={{ theme: "light" }}
                            onSuccess={(token) => setCaptchaToken(token)}
                            onExpire={() => setCaptchaToken(null)}
                            onError={() => setCaptchaToken(null)}
                        />
                    </div>
                )}

                <button
                    type="submit"
                    disabled={loading || !captchaToken}
                    className="w-full bg-[#859F4A] hover:bg-[#6c823b] text-white py-3.5 rounded-xl font-bold text-sm tracking-wide transition-all shadow-md disabled:opacity-50 cursor-pointer"
                >
                    {loading ? t("auth.signingIn") : isSignUp ? t("auth.signUp") : t("auth.signIn")}
                </button>
            </form>

            {!isSignUp && (
                <div className="mt-3 text-center">
                    <button
                        type="button"
                        onClick={handleResetPassword}
                        disabled={loading || !captchaToken}
                        className="text-[11px] font-bold uppercase tracking-wider text-slate-500 hover:text-[#859F4A] transition-colors cursor-pointer disabled:opacity-40"
                    >
                        {t("auth.forgotPassword")}
                    </button>
                </div>
            )}

            <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                    {t("auth.or")}
                </span>
                <div className="flex-1 h-px bg-slate-200" />
            </div>

            <div className="space-y-2">
                <button
                    type="button"
                    onClick={handleMagicLink}
                    disabled={loading || !captchaToken}
                    className="w-full bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 py-3 rounded-xl font-bold text-xs uppercase tracking-wider transition-all shadow-xs disabled:opacity-50 cursor-pointer"
                >
                    {t("auth.magicLink")}
                </button>
                <button
                    type="button"
                    onClick={handlePasskeySignIn}
                    disabled={loading || !captchaToken}
                    className="w-full bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 py-3 rounded-xl font-bold text-xs uppercase tracking-wider transition-all shadow-xs disabled:opacity-50 cursor-pointer flex items-center justify-center gap-2"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="7.5" cy="15.5" r="5.5" />
                        <path d="m21 2-9.6 9.6" />
                        <path d="m15.5 7.5 3 3L22 7l-3-3" />
                    </svg>
                    {t("auth.passkey")}
                </button>
                <button
                    type="button"
                    onClick={handleGoogle}
                    disabled={loading}
                    className="w-full bg-white border border-slate-300 hover:bg-gray-50 text-slate-700 py-3 rounded-xl font-bold text-xs uppercase tracking-wider transition-all shadow-xs disabled:opacity-50 cursor-pointer flex items-center justify-center gap-2"
                >
                    <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
                        <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z" />
                        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
                        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.6 39.6 16.2 44 24 44z" />
                        <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C41.4 34.9 44 29.9 44 24c0-1.3-.1-2.6-.4-3.9z" />
                    </svg>
                    {t("auth.google")}
                </button>
            </div>
        </div>
    );
}
